import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import { AnimatePresence, motion } from "framer-motion";
import { LanguageProvider } from "./contexts/LanguageContext";
import { CurrencyProvider } from "./contexts/CurrencyContext";
import { isSupportedLang, DEFAULT_LANG } from "./i18n";
import HomePage from "./pages/HomePage";
import ServiceLandingPage from "./pages/ServiceLandingPage";
import StickyVerticalCTA from "./components/StickyVerticalCTA";
import ServicesIndexPage from "./pages/ServicesIndexPage";
import AboutPage from "./pages/AboutPage";
import ContactPage from "./pages/ContactPage";
import PostunHazirPage from "./pages/PostunHazirPage";
import WorksIndexPage from "./pages/WorksIndexPage";
import WorksDetailPage from "./pages/WorksDetailPage";
import BlogIndexPage from "./pages/BlogIndexPage";
import BlogArticlePage from "./pages/BlogArticlePage";
import AdminLoginPage from "./pages/admin/AdminLoginPage";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminDashboardPage from "./pages/admin/AdminDashboardPage";
import AdminBlogListPage from "./pages/admin/AdminBlogListPage";
import AdminBlogEditorPage from "./pages/admin/AdminBlogEditorPage";
import AdminLeadsPage from "./pages/admin/AdminLeadsPage";
import AdminWorksPage from "./pages/admin/AdminWorksPage";
import AdminClientsPage from "./pages/admin/AdminClientsPage";
import AdminTeamPage from "./pages/admin/AdminTeamPage";
import AdminReviewsPage from "./pages/admin/AdminReviewsPage";
import AdminSettingsPage from "./pages/admin/AdminSettingsPage";
import AdminLiveEditorPage from "./pages/admin/AdminLiveEditorPage";
import AdminServicesPage from "./pages/admin/AdminServicesPage";
import AdminServiceEditPage from "./pages/admin/AdminServiceEditPage";
import AdminPricingPage from "./pages/admin/AdminPricingPage";
import AdminSiteContentPage from "./pages/admin/AdminSiteContentPage";
import AdminTranslationsPage from "./pages/admin/AdminTranslationsPage";
import AdminChangePasswordPage from "./pages/admin/AdminChangePasswordPage";
import RequireAdmin from "./components/admin/RequireAdmin";
import { AdminAuthProvider } from "./contexts/AdminAuthContext";
import PrivacyPage from "./pages/PrivacyPage";
import TermsPage from "./pages/TermsPage";
import PackageFinderPage from "./pages/PackageFinderPage";
import ThankYouPage from "./pages/ThankYouPage";
import LenisScroller from "./components/LenisScroller";
import ScrollToTop from "./components/ScrollToTop";
import WhatsAppFloatingCTA from "./components/atoms/WhatsAppFloatingCTA";
import ScrollProgressBar from "./components/ScrollProgressBar";
import AnnouncementBar from "./components/AnnouncementBar";
import ErrorBoundary from "./components/ErrorBoundary";

function withLang(Component) {
  return function Wrapped() {
    const { lang } = useParams();
    if (!isSupportedLang(lang)) {
      return <Navigate to={`/${DEFAULT_LANG}/`} replace />;
    }
    return (
      <LanguageProvider lang={lang}>
        <CurrencyProvider lang={lang}>
          <Component />
        </CurrencyProvider>
      </LanguageProvider>
    );
  };
}

const PrivacyRoute = withLang(PrivacyPage);
const TermsRoute = withLang(TermsPage);
const PackageFinderRoute = withLang(PackageFinderPage);
const ThankYouRoute = withLang(ThankYouPage);

const HomeRoute = withLang(HomePage);
const ServiceRoute = withLang(ServiceLandingPage);
const ServicesIndexRoute = withLang(ServicesIndexPage);
const AboutRoute = withLang(AboutPage);
const ContactRoute = withLang(ContactPage);
const PostunHazirRoute = withLang(PostunHazirPage);
const WorksIndexRoute = withLang(WorksIndexPage);
const WorksDetailRoute = withLang(WorksDetailPage);
const BlogIndexRoute = withLang(BlogIndexPage);
const BlogArticleRoute = withLang(BlogArticlePage);

function PageTransition({ children }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      className="page-wrap"
    >
      {children}
    </motion.div>
  );
}

function AppRoutes() {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");
  return (
    <>
      <ScrollToTop />
      <AnimatePresence mode="wait">
        <Routes key={location.pathname} location={location}>
          {/* Admin routes */}
          <Route path="/admin/login" element={<AdminLoginPage />} />
          <Route path="/admin" element={<RequireAdmin><AdminLayout /></RequireAdmin>}>
            <Route index element={<Navigate to="/admin/dashboard" replace />} />
            <Route path="dashboard" element={<AdminDashboardPage />} />
            <Route path="blog" element={<AdminBlogListPage />} />
            <Route path="blog/new" element={<AdminBlogEditorPage />} />
            <Route path="blog/:id/edit" element={<AdminBlogEditorPage />} />
            <Route path="leads" element={<AdminLeadsPage />} />
            <Route path="works" element={<AdminWorksPage />} />
            <Route path="clients" element={<AdminClientsPage />} />
            <Route path="team" element={<AdminTeamPage />} />
            <Route path="reviews" element={<AdminReviewsPage />} />
            <Route path="services" element={<AdminServicesPage />} />
            <Route path="services/:slug/edit" element={<AdminServiceEditPage />} />
            <Route path="pricing" element={<AdminPricingPage />} />
            <Route path="site-content" element={<AdminSiteContentPage />} />
            <Route path="translations" element={<AdminTranslationsPage />} />
            <Route path="settings" element={<AdminSettingsPage />} />
            <Route path="live-editor" element={<AdminLiveEditorPage />} />
            <Route path="change-password" element={<AdminChangePasswordPage />} />
          </Route>

          {/* Public site */}
          <Route path="/" element={<Navigate to={`/${DEFAULT_LANG}/`} replace />} />

          {/* 301-like redirects for common old URL patterns (no lang prefix) */}
          <Route path="/hizmetler" element={<Navigate to={`/${DEFAULT_LANG}/services`} replace />} />
          <Route path="/hizmetler/*" element={<Navigate to={`/${DEFAULT_LANG}/services`} replace />} />
          <Route path="/hakkimizda" element={<Navigate to={`/${DEFAULT_LANG}/about`} replace />} />
          <Route path="/hakkimizda/" element={<Navigate to={`/${DEFAULT_LANG}/about`} replace />} />
          <Route path="/iletisim" element={<Navigate to={`/${DEFAULT_LANG}/contact`} replace />} />
          <Route path="/iletisim/" element={<Navigate to={`/${DEFAULT_LANG}/contact`} replace />} />
          <Route path="/blog/*" element={<Navigate to={`/${DEFAULT_LANG}/blog`} replace />} />
          <Route path="/calismalari" element={<Navigate to={`/${DEFAULT_LANG}/works`} replace />} />
          <Route path="/calismalarimiz" element={<Navigate to={`/${DEFAULT_LANG}/works`} replace />} />
          <Route path="/services" element={<Navigate to={`/${DEFAULT_LANG}/services`} replace />} />
          <Route path="/about" element={<Navigate to={`/${DEFAULT_LANG}/about`} replace />} />
          <Route path="/contact" element={<Navigate to={`/${DEFAULT_LANG}/contact`} replace />} />
          <Route path="/postun-hazir" element={<Navigate to={`/${DEFAULT_LANG}/postun-hazir`} replace />} />
          <Route path="/:lang" element={<PageTransition><HomeRoute /></PageTransition>} />
          <Route path="/:lang/" element={<PageTransition><HomeRoute /></PageTransition>} />
          <Route path="/:lang/services" element={<PageTransition><ServicesIndexRoute /></PageTransition>} />
          <Route path="/:lang/services/" element={<PageTransition><ServicesIndexRoute /></PageTransition>} />
          <Route path="/:lang/services/:slug" element={<PageTransition><ServiceRoute /></PageTransition>} />
          <Route path="/:lang/services/:slug/" element={<PageTransition><ServiceRoute /></PageTransition>} />
          <Route path="/:lang/about" element={<PageTransition><AboutRoute /></PageTransition>} />
          <Route path="/:lang/about/" element={<PageTransition><AboutRoute /></PageTransition>} />
          <Route path="/:lang/contact" element={<PageTransition><ContactRoute /></PageTransition>} />
          <Route path="/:lang/contact/" element={<PageTransition><ContactRoute /></PageTransition>} />
          <Route path="/:lang/postun-hazir" element={<PageTransition><PostunHazirRoute /></PageTransition>} />
          <Route path="/:lang/postun-hazir/" element={<PageTransition><PostunHazirRoute /></PageTransition>} />
          <Route path="/:lang/blog" element={<PageTransition><BlogIndexRoute /></PageTransition>} />
          <Route path="/:lang/blog/" element={<PageTransition><BlogIndexRoute /></PageTransition>} />
          <Route path="/:lang/blog/:slug" element={<PageTransition><BlogArticleRoute /></PageTransition>} />
          <Route path="/:lang/works" element={<PageTransition><WorksIndexRoute /></PageTransition>} />
          <Route path="/:lang/works/" element={<PageTransition><WorksIndexRoute /></PageTransition>} />
          <Route path="/:lang/works/:slug" element={<PageTransition><WorksDetailRoute /></PageTransition>} />
          <Route path="/:lang/privacy" element={<PageTransition><PrivacyRoute /></PageTransition>} />
          <Route path="/:lang/terms" element={<PageTransition><TermsRoute /></PageTransition>} />
          <Route path="/:lang/paketini-bul" element={<PageTransition><PackageFinderRoute /></PageTransition>} />
          <Route path="/:lang/tesekkurler" element={<PageTransition><ThankYouRoute /></PageTransition>} />
          <Route path="*" element={<Navigate to={`/${DEFAULT_LANG}/`} replace />} />
        </Routes>
      </AnimatePresence>
      {!isAdmin && <WhatsAppFloatingCTA />}
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <div className="App">
          <BrowserRouter>
            <AdminAuthProvider>
              <LenisScroller />
              <ScrollProgressBar />
              <AnnouncementBar />
              <AppRoutes />
              <StickyVerticalCTA />
            </AdminAuthProvider>
          </BrowserRouter>
          <Toaster richColors position="top-center" />
        </div>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
